import React from 'react';
import TrendItem from './TrendItem';
import { Selection } from '../types';

/**
 * Props for the TrendPair component
 */
interface TrendPairProps {
  /**
   * The search term to display on the left side
   */
  leftContent: string;
  
  /**
   * The search term to display on the right side
   */
  rightContent: string;
  
  /**
   * Index of this pair in the game board
   * Used to identify which pair was selected when calling onSelection
   */
  pairIndex: number;
  
  /**
   * Which side the user selected (if any)
   * Undefined if no selection has been made yet
   */
  selectedSide?: Selection;
  
  /**
   * Whether the left item has higher search popularity than the right
   * Used to determine if the user's selection was correct
   */
  correctIsLeft: boolean;
  
  /**
   * Callback function invoked when user selects either the left or right item
   * @param pairIndex - Index of the pair that was selected
   * @param isLeft - Whether the left item (true) or right item (false) was selected
   */
  onSelection: (pairIndex: number, isLeft: boolean) => void;
  
  /**
   * Whether to show the actual popularity scores
   * Typically true after a selection has been made
   */
  showScores?: boolean;
  
  /**
   * The popularity score (0-100) for the left search term
   */
  leftScore?: number;
  
  /**
   * The popularity score (0-100) for the right search term
   */
  rightScore?: number;
  
  /**
   * Whether to show the percentages of other users' guesses
   * Typically true when the game is complete
   */
  showPercentages?: boolean;
  
  /**
   * Percentage of users who chose the left term
   */
  leftPercentage?: number;
  
  /**
   * Percentage of users who chose the right term
   */
  rightPercentage?: number;
}

/**
 * TrendPair: A component that displays a pair of trend terms for comparison
 * 
 * This component presents two search terms side by side for the user to compare.
 * It handles:
 * - Displaying the terms as clickable items
 * - Showing which one the user selected (if any)
 * - Indicating whether the selection was correct
 * - Displaying the actual popularity scores after selection
 * - Showing statistics about other users' choices
 * 
 * The component demonstrates:
 * - Composing smaller components (TrendItem) to build a larger UI
 * - Conditional rendering based on props
 * - Passing callbacks up to parent components
 * - Flexible styling based on component state
 */
const TrendPair: React.FC<TrendPairProps> = ({
  leftContent,
  rightContent,
  pairIndex,
  selectedSide,
  correctIsLeft,
  onSelection,
  showScores = false,
  leftScore = 0,
  rightScore = 0,
  showPercentages = false,
  leftPercentage = 0,
  rightPercentage = 0,
}) => {
  return (
    <div className="trend-pair-container" style={{ marginBottom: '20px' }}>
      {/* Main container for the pair - displays items side by side */}
      <div 
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '20px',
        }}
      >
        {/* Left Trend Section */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {/* Left search term as clickable item */}
          <TrendItem
            content={leftContent}
            onClick={() => onSelection(pairIndex, true)}
            isSelected={selectedSide === 'left'}
            isCorrect={selectedSide === 'left' && correctIsLeft}
            isDisabled={selectedSide !== undefined}
          />
          
          {/* Show popularity score after selection (conditionally) */}
          {showScores && (
            <div style={{ 
              marginTop: '8px', 
              fontWeight: 'bold',
              // Green if correctly selected, gray otherwise
              color: selectedSide === 'left' && correctIsLeft ? '#4CAF50' : '#666'
            }}>
              Score: {leftScore.toFixed(1)}
            </div>
          )}
          
          {/* Show percentage of other users who selected this (conditionally) */}
          {showPercentages && (
            <div style={{ 
              marginTop: '4px', 
              fontSize: '14px',
              color: '#666'
            }}>
              {leftPercentage.toFixed(1)}% of players chose this
            </div>
          )}
        </div>
        
        {/* Versus Indicator - Displayed between the two terms */}
        <div style={{ 
          fontSize: '24px', 
          fontWeight: 'bold',
          color: '#666'
        }}>
          VS
        </div>
        
        {/* Right Trend Section */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {/* Right search term as clickable item */}
          <TrendItem
            content={rightContent}
            onClick={() => onSelection(pairIndex, false)}
            isSelected={selectedSide === 'right'}
            isCorrect={selectedSide === 'right' && !correctIsLeft}
            isDisabled={selectedSide !== undefined}
          />
          
          {/* Show popularity score after selection (conditionally) */}
          {showScores && (
            <div style={{ 
              marginTop: '8px', 
              fontWeight: 'bold',
              // Green if correctly selected, gray otherwise
              color: selectedSide === 'right' && !correctIsLeft ? '#4CAF50' : '#666'
            }}>
              Score: {rightScore.toFixed(1)}
            </div>
          )}
          
          {/* Show percentage of other users who selected this (conditionally) */}
          {showPercentages && (
            <div style={{ 
              marginTop: '4px', 
              fontSize: '14px',
              color: '#666'
            }}>
              {rightPercentage.toFixed(1)}% of players chose this
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrendPair;